import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  BusinessImageUsage,
  ImageGenerationQuery,
  ImageGenerationRow,
  ImageSettings,
  ImageSettingsUpdate
} from '../models/ai-images.model';

/** Rows asked for per list; the account API allows 1–500 (CONTRACT-v1.4 §3). */
export const IMAGE_GENERATIONS_TAKE = 500;

/**
 * Staff surface of the AI images add-on (specs/agents CONTRACT-v1.4 §5.4, owner decision 2026-10-28). Two hosts:
 *  - reads on the AI API, `api/admin/images/*`: the AI API holds no database of its own and reads the account
 *    API's allowance ledger and generation list (§3) and Retail.API's media for the thumbnails;
 *  - the platform settings (switch, quality, brand terms, agent-run defaults) on the account API,
 *    `GET|PUT api/admin/agents/images/settings`, which owns `agent.ImageSettings` and audits each change.
 * The bearer token goes on exactly as for every other admin call (AuthInterceptor); all of it is platform
 * staff only. Prices are set under Billing, the allowance on the plan editor, the model under AI › Assignments.
 */
@Injectable({ providedIn: 'root' })
export class AiImagesAdminService {
  private readonly baseUrl = environment.aiApiBaseUrl + '/api/admin/images';
  private readonly settingsUrl = environment.subscriptionApiUrl + '/api/admin/agents/images/settings';

  constructor(private http: HttpClient) {}

  /**
   * One business's allowance (account-wide, this UTC month), the provider cost of the range and its
   * generations with thumbnails. `from`/`to` are ISO instants.
   */
  getUsage(businessId: string, from: string, to: string): Observable<BusinessImageUsage> {
    const params = new HttpParams().set('businessId', businessId).set('from', from).set('to', to);
    return this.http.get<BusinessImageUsage>(`${this.baseUrl}/usage`, { params });
  }

  /** The generation list, newest first; only the filters that are set are sent. */
  listGenerations(query: ImageGenerationQuery = {}): Observable<ImageGenerationRow[]> {
    let params = new HttpParams();
    const keys: Array<keyof ImageGenerationQuery> = ['businessId', 'accountUserId', 'status', 'from', 'to', 'take'];
    for (const key of keys) {
      const value = query[key];
      if (value === null || value === undefined || value === '') continue;
      params = params.set(key, String(value));
    }
    return this.http.get<ImageGenerationRow[]>(`${this.baseUrl}/generations`, { params });
  }

  /** The platform settings and who last changed them. */
  getSettings(): Observable<ImageSettings> {
    return this.http.get<ImageSettings>(this.settingsUrl);
  }

  /** Replaces the settings; the account API validates (1 ≤ default ≤ cap ≤ 8) and answers with the stored row. */
  saveSettings(settings: ImageSettingsUpdate): Observable<ImageSettings> {
    return this.http.put<ImageSettings>(this.settingsUrl, settings);
  }
}
